import type { Animation, AnimationStep, GameObject } from "./types.ts";
import { findState } from "./states.ts";
// An animation is a chain of steps played one after another. Every step
// starts from where the previous one ended, so a frame at any moment can be
// computed from the starting pose alone: the editor preview and the game use
// the same function and always agree.
export type Pose = Pick<
  GameObject,
  "x" | "y" | "rotation" | "opacity" | "visible" | "state" | "shadow"
>;
export const poseOf = (o: GameObject): Pose => ({
  x: o.x,
  y: o.y,
  rotation: o.rotation,
  opacity: o.opacity,
  visible: o.visible,
  state: o.state,
  shadow: o.shadow,
});
export const stepKinds = {
  move: "Переместить в точку",
  offset: "Сдвинуть на",
  return: "Вернуть на место",
  rotate: "Повернуть на",
  opacity: "Прозрачность",
  show: "Появиться",
  hide: "Исчезнуть",
  shadow: "Тень",
  state: "Сменить картинку",
  wait: "Пауза",
} as const;
export const easings = {
  linear: "Равномерно",
  easeOut: "Замедление",
  easeIn: "Ускорение",
  easeInOut: "Плавно",
} as const;
const ease = (kind: AnimationStep["easing"], t: number) =>
  kind === "easeIn"
    ? t * t
    : kind === "easeOut"
      ? 1 - (1 - t) * (1 - t)
      : kind === "easeInOut"
        ? t < 0.5
          ? 2 * t * t
          : 1 - 2 * (1 - t) * (1 - t)
        : t;
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
export const stepDefaults = (
  kind: AnimationStep["kind"] = "offset",
): AnimationStep => ({
  id: crypto.randomUUID(),
  kind,
  duration: kind === "state" || kind === "return" ? 0.25 : 0.5,
  easing: "easeInOut",
  x: kind === "offset" ? 40 : 0,
  y: 0,
  value:
    kind === "rotate"
      ? 90
      : kind === "opacity"
        ? 0.5
        : kind === "shadow"
          ? 12
          : 0,
  state: "",
});
export const animationDefaults = (name = "Анимация"): Animation => ({
  id: crypto.randomUUID(),
  name,
  loop: false,
  steps: [stepDefaults()],
});
export const duration = (a: Animation) =>
  a.steps.reduce((sum, s) => sum + s.duration, 0);
/** Where a step leaves the layer when it has fully played. */
function target(
  s: AnimationStep,
  from: Pose,
  origin: Pose,
  o: GameObject,
): Pose {
  switch (s.kind) {
    case "move":
      return { ...from, x: s.x, y: s.y };
    case "offset":
      return { ...from, x: from.x + s.x, y: from.y + s.y };
    case "return":
      return { ...from, x: origin.x, y: origin.y, rotation: origin.rotation };
    case "rotate":
      return { ...from, rotation: from.rotation + s.value };
    case "opacity":
      return { ...from, opacity: Math.max(0, Math.min(1, s.value)) };
    case "show":
      return { ...from, visible: true, opacity: from.visible ? from.opacity : origin.opacity || 1 };
    case "hide":
      // Hidden layers keep their opacity so that showing them brings it back.
      return { ...from, visible: false };
    case "shadow":
      return { ...from, shadow: Math.max(0, Math.min(100, s.value)) };
    case "state":
      return {
        ...from,
        state: s.state.trim() ? (findState(o, s.state)?.id ?? from.state) : null,
      };
    case "wait":
      return from;
  }
}
function between(
  s: AnimationStep,
  from: Pose,
  to: Pose,
  t: number,
): Pose {
  const k = ease(s.easing, t);
  if (s.kind === "show")
    return {
      ...to,
      opacity: from.visible ? to.opacity : mix(0, to.opacity, k),
    };
  if (s.kind === "hide")
    return t >= 1
      ? to
      : { ...from, opacity: mix(from.opacity, 0, k) };
  // Pictures switch at the start of a step; the duration is how long it holds.
  if (s.kind === "state") return to;
  return {
    ...to,
    x: mix(from.x, to.x, k),
    y: mix(from.y, to.y, k),
    rotation: mix(from.rotation, to.rotation, k),
    opacity: mix(from.opacity, to.opacity, k),
    shadow: mix(from.shadow, to.shadow, k),
  };
}
/**
 * Pose of a layer `elapsed` seconds into an animation that started from
 * `start`. `origin` is the pose at scene start, used by «Вернуть на место».
 * Looping animations restart from `start` on every lap.
 */
export function sample(
  o: GameObject,
  a: Animation,
  start: Pose,
  origin: Pose,
  elapsed: number,
): { pose: Pose; done: boolean; step: number } {
  const total = duration(a);
  let t = Math.max(0, elapsed);
  if (a.loop && total > 0) t %= total;
  let pose = start;
  for (let i = 0; i < a.steps.length; i++) {
    const s = a.steps[i],
      to = target(s, pose, origin, o);
    if (t < s.duration)
      return { pose: between(s, pose, to, t / s.duration), done: false, step: i };
    t -= s.duration;
    pose = to;
  }
  return { pose, done: !a.loop || total === 0, step: a.steps.length };
}
export function findAnimation(o: GameObject, name: string) {
  const key = name.trim().toLocaleLowerCase("ru");
  return (
    o.animations?.find((a) => a.name.trim().toLocaleLowerCase("ru") === key) ||
    null
  );
}
